import { Link } from 'react-router';
import type { PlayerWithStats } from '@repo/types';
import { usePlayerListQuery } from '@data/footballPlayers/usePlayerListQuery';
// Shared with the SSR prefetch — the query config object is part of the
// React Query key hash, so both sides must use the identical object.
import { TOP_SCORERS_QUERY } from './megaPrefetch';

export type NavMegaScorersProps = {
  competitionId?: string;
  /** Closes the mega menu panel — forwarded to the player link, matching the raw link columns. */
  onNavigate?: () => void;
};

type ScorerNameProps = { player: PlayerWithStats; onNavigate?: () => void };

// Never emit a dead link: only players carrying the master football_players id link out.
function ScorerName({ player, onNavigate }: ScorerNameProps) {
  if (!player.id) {
    return <span>{player.name}</span>;
  }

  return (
    <Link className="nav__scorers-link" to={`/players/${player.id}`} onClick={onNavigate}>
      {player.name}
    </Link>
  );
}

export function NavMegaScorers({ competitionId, onNavigate }: NavMegaScorersProps) {
  const { data, isLoading, error } = usePlayerListQuery({ query: TOP_SCORERS_QUERY, competition: competitionId });

  if (isLoading || error || !data || data.items.length === 0) {
    return null;
  }

  return (
    <div className="nav__mega-comp">
      <h6 className="nav__mega-title">Top scorers</h6>
      <ul className="nav__scorers">
        {data.items.map((player, index) => (
          <li key={player.id} className="text-3xs">
            <span className="nav__scorers-rank">{index + 1}</span>
            <span className="nav__scorers-name">
              <ScorerName player={player} onNavigate={onNavigate} />
              {player.team && <span className="nav__scorers-team text-3xs">{player.team.fullName}</span>}
            </span>
            <span className="nav__scorers-goals">{player.stats['goals'] ?? 0}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
